import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocalSearchParams, useRouter } from 'expo-router';
import {
  AlertCircle,
  ArrowLeft,
  Briefcase,
  Building2,
  CheckCircle,
  Clock,
  Edit3,
  ExternalLink,
  Globe,
  Mail,
  Phone,
  PhoneCall,
  ShieldCheck,
  Trash2,
  User,
  X,
} from 'lucide-react-native';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  type StyleProp,
  StyleSheet,
  useWindowDimensions,
  View,
  type ViewStyle,
} from 'react-native';
import { InstagramIcon, LinkedinIcon } from '../../../../../src/components/icons/BrandIcons';
import {
  Avatar,
  Badge,
  Box,
  Card,
  HStack,
  IconButton,
  Stack,
  Text,
  VStack,
} from '../../../../../src/components/primitives';
import { Button } from '../../../../../src/components/primitives/Button';
import { FormInput } from '../../../../../src/components/primitives/FormInput';
import { Sheet } from '../../../../../src/components/primitives/Sheet';
import { useAuth } from '../../../../../src/contexts/AuthContext';
import { useModal } from '../../../../../src/contexts/ModalContext';
import { useToast } from '../../../../../src/contexts/ToastContext';
import { useTranslation } from '../../../../../src/contexts/TranslationContext';
import { AgentInsightsCard } from '../../../../../src/features/agents/components/AgentInsightsCard';
import { useLeadAgentStatus } from '../../../../../src/features/agents/hooks/useAgents';
import { AnalysisCard } from '../../../../../src/features/leads/components/AnalysisCard';
import { AssigneePicker } from '../../../../../src/features/leads/components/AssigneePicker';
import { CallMethodSheet } from '../../../../../src/features/leads/components/CallMethodSheet';
import { CallResultsSection } from '../../../../../src/features/leads/components/CallResultsSection';
import {
  type CompanyAnalysisData,
  CompanyProfileCard,
  type CompanyProfileData,
} from '../../../../../src/features/leads/components/CompanyProfileCard';
import { ConsentSection } from '../../../../../src/features/leads/components/ConsentSection';
import { ContactCarouselCard } from '../../../../../src/features/leads/components/ContactCarouselCard';
import { CustomFieldsCard } from '../../../../../src/features/leads/components/CustomFieldsCard';
import { EnrichmentFailedActions } from '../../../../../src/features/leads/components/EnrichmentFailedActions';
import {
  ENRICHMENT_FIELD_KEYS,
  type EnrichmentData,
  EnrichmentProfileCard,
} from '../../../../../src/features/leads/components/EnrichmentProfileCard';
import { InstagramContentCarouselCard } from '../../../../../src/features/leads/components/InstagramContentCarouselCard';
import { LeadActivitySection } from '../../../../../src/features/leads/components/LeadActivitySection';
import { LeadDetailSkeleton } from '../../../../../src/features/leads/components/LeadSkeletons';
import { LeadSourceBadges } from '../../../../../src/features/leads/components/LeadSourceBadges';
import { LeadTagsEditor } from '../../../../../src/features/leads/components/LeadTagsEditor';
import {
  LinkedInActivityCarouselCard,
  type RefreshTarget,
} from '../../../../../src/features/leads/components/LinkedInActivityCarouselCard';
import type { LinkedInPostData } from '../../../../../src/features/leads/components/LinkedInPostsCard';
import { MarketingConsentSection } from '../../../../../src/features/leads/components/MarketingConsentSection';
import { NotesList } from '../../../../../src/features/leads/components/NotesList';
import { PlatformIcon } from '../../../../../src/features/leads/components/PlatformIcon';
import { ProductInterestField } from '../../../../../src/features/leads/components/ProductInterestField';
import { ReviewFlagBanner } from '../../../../../src/features/leads/components/ReviewFlagBanner';
import { StageMoveModal } from '../../../../../src/features/leads/components/StageMoveModal';
import { StageProgress } from '../../../../../src/features/leads/components/StageProgress';
import {
  getBrandBio,
  getBrandCategory,
  isBrandLead,
} from '../../../../../src/features/leads/utils/brandLead';
import {
  firstParam,
  resolveCommenterDeepLink,
  resolveCommenterEnrichmentAction,
  resolveCommenterProfileUrl,
} from '../../../../../src/features/leads/utils/commenterEnrichment';
import { readCompanyUrls } from '../../../../../src/features/leads/utils/companyProfileUrls';
import { collectContactEnrichments } from '../../../../../src/features/leads/utils/contactEnrichments';
import { asCustomFields } from '../../../../../src/features/leads/utils/customFields';
import { resolveEnrichmentFailureMessage } from '../../../../../src/features/leads/utils/enrichmentError';
import { collectInstagramPosts } from '../../../../../src/features/leads/utils/instagramPosts';
import { collectInstagramRecommendations } from '../../../../../src/features/leads/utils/instagramRecommendations';
import { collectInstagramReels } from '../../../../../src/features/leads/utils/instagramReels';
import { getLeadHeaderIdentity } from '../../../../../src/features/leads/utils/leadHeaderIdentity';
import { getLeadProfileUrls } from '../../../../../src/features/leads/utils/leadPlatforms';
import { voiceApi } from '../../../../../src/features/voice/api/voiceApi';
import { CallStatusBanner } from '../../../../../src/features/voice/components/CallStatusBanner';
import { WebSearchButton } from '../../../../../src/features/web-search/components/WebSearchButton';
import { WebSearchProgressSheet } from '../../../../../src/features/web-search/components/WebSearchProgressSheet';
import { WebSearchResultsSheet } from '../../../../../src/features/web-search/components/WebSearchResultsSheet';
import {
  useCancelWebSearch,
  useConfirmWebSearch,
  useTriggerWebSearch,
  useWebSearchResults,
} from '../../../../../src/features/web-search/hooks/useWebSearch';
import { useWebSearchStream } from '../../../../../src/features/web-search/hooks/useWebSearchStream';
import type {
  ExtractedContact,
  WebSearchResult as WebSearchResultType,
  WebSearchStatusEvent,
} from '../../../../../src/features/web-search/types';
import { useCurrentOrgRole } from '../../../../../src/hooks/useAuthOrgRole';
import { useBoardRealtime } from '../../../../../src/hooks/useBoardRealtime';
import {
  type EnrichmentStatusEvent,
  useEnrichmentStream,
} from '../../../../../src/hooks/useEnrichmentStream';
import { useForceQualifyLead } from '../../../../../src/hooks/useJobTitleFilters';
import {
  useAddNote,
  useDeleteLead,
  useInvalidateLeadDetail,
  useLead,
  useMoveStage,
  useRefreshPosts,
  useRescanContact,
  useUpdateLead,
} from '../../../../../src/hooks/useLeads';
import { useOriginBack } from '../../../../../src/hooks/useOriginBack';
import { useTriggerWorkflow } from '../../../../../src/hooks/useTriggerWorkflow';
import { trackEvent } from '../../../../../src/lib/analytics';
import { useMixpanel } from '../../../../../src/lib/mixpanel';
import { workflowsAPI } from '../../../../../src/lib/workflowsApi';
import { colors } from '../../../../../src/theme/colors';
import { tokens } from '../../../../../src/theme/tokens';
import type { AnalysisResult } from '../../../../../src/types/leads';

function InfoField({
  icon,
  label,
  value,
  testID,
}: Readonly<{
  icon: ReactNode;
  label: string;
  value: string | null | undefined;
  testID?: string;
}>) {
  if (!value) return null;
  return (
    <HStack spacing="md" align="center" testID={testID}>
      {icon}
      <VStack spacing="xxs" style={{ flex: 1 }}>
        <Text variant="label" uppercase color={colors.text.tertiary}>
          {label}
        </Text>
        <Text variant="body" color={colors.text.primary}>
          {value}
        </Text>
      </VStack>
    </HStack>
  );
}

/**
 * A section of the lead detail screen.
 *
 * This replaces a hand-rolled bordered `View` that predated `cardSurface()`.
 * Because it was hand-rolled it also got wrapped around components that were
 * already cards, so most of this screen rendered a border inside a border —
 * which is what made it read as a wall of boxes. There is now exactly one card
 * per section, built by the primitive, and the section title lives here instead
 * of being re-declared at six call sites.
 *
 * Sections that own their own card (analysis, enrichment, contacts, activity
 * feeds) are rendered bare — they must never be wrapped in this.
 */
function SectionCard({
  title,
  icon,
  action,
  variant,
  children,
  style,
  testID,
}: Readonly<{
  title?: string;
  icon?: ReactNode;
  action?: ReactNode;
  variant?: 'default' | 'accent';
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}>) {
  return (
    <Card
      variant={variant}
      padding="lg"
      radius="xl"
      hoverable={false}
      style={style}
      testID={testID}
    >
      {title ? (
        <VStack spacing="lg">
          <HStack spacing="sm" align="center" justify="between">
            <HStack spacing="sm" align="center" style={{ flex: 1, minWidth: 0 }}>
              {icon}
              <Text
                variant="h6"
                color={colors.text.primary}
                numberOfLines={1}
                style={{ fontWeight: '600' }}
              >
                {title}
              </Text>
            </HStack>
            {action}
          </HStack>
          {children}
        </VStack>
      ) : (
        children
      )}
    </Card>
  );
}

export default function LeadDetailScreen() {
  const {
    id,
    source,
    publicId,
    name: commenterNameParam,
    from,
  } = useLocalSearchParams<{
    id: string;
    source?: string;
    publicId?: string;
    name?: string;
    from?: string;
  }>();
  const router = useRouter();
  const goBack = useOriginBack(from);
  const {
    isAuthenticated,
    isLoading: authLoading,
    currentOrganization,
    backendUser,
    getAccessToken,
  } = useAuth();
  const { confirm } = useModal();
  const { t } = useTranslation();
  const { showError } = useToast();
  const { width } = useWindowDimensions();
  const isDesktop = width >= tokens.breakpoints.desktop;
  const isMobile = width < tokens.breakpoints.tablet;

  const mp = useMixpanel();
  const { data: lead, isLoading, error } = useLead(id, currentOrganization?.id);
  const addNote = useAddNote();
  const deleteLead = useDeleteLead();
  const refreshPosts = useRefreshPosts();
  const rescanContact = useRescanContact();
  const forceQualify = useForceQualifyLead();
  const updateLead = useUpdateLead();
  const queryClient = useQueryClient();

  const [showForceQualifyModal, setShowForceQualifyModal] = useState(false);
  const [forceQualifyReason, setForceQualifyReason] = useState('');
  const [pendingMoveStageId, setPendingMoveStageId] = useState<string | null>(null);
  const orgRole = useCurrentOrgRole();
  const isPrivileged = orgRole === 'owner' || orgRole === 'admin';

  const moveStage = useMoveStage({
    onQualificationBlocked: ({ variables }) => {
      if (!isPrivileged) return false;
      setPendingMoveStageId(variables.stageId);
      setShowForceQualifyModal(true);
      return true;
    },
  });

  const { data: agentStatus } = useLeadAgentStatus(id ?? '', lead?.organizationId ?? '');
  const pipelineRunning = !!agentStatus?.pipelineRunning;
  const prevPipelineRunningRef = useRef(pipelineRunning);
  const invalidateLeadDetail = useInvalidateLeadDetail();

  useEffect(() => {
    if (prevPipelineRunningRef.current && !pipelineRunning && id) {
      invalidateLeadDetail(id);
    }
    prevPipelineRunningRef.current = pipelineRunning;
  }, [pipelineRunning, id, invalidateLeadDetail]);

  const enrichmentStatus = asCustomFields(lead?.customFields).enrichmentStatus as
    | string
    | undefined;
  const isEnrichmentProcessing = enrichmentStatus === 'processing';

  useEnrichmentStream({
    organizationId: currentOrganization?.id ?? '',
    enabled: isEnrichmentProcessing,
    getAccessToken,
  });

  useBoardRealtime({
    organizationId: currentOrganization?.id,
    enabled: !!currentOrganization?.id,
    getAccessToken,
  });

  const [showStageModal, setShowStageModal] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState<'updated' | 'no_changes' | 'error' | null>(
    null,
  );

  type EnrichmentPhase =
    | 'idle'
    | 'checking'
    | 'exists'
    | 'submitted'
    | 'scraping'
    | 'scraping_comments'
    | 'analyzing'
    | 'persisting'
    | 'completed'
    | 'failed';
  const [enrichPhase, setEnrichPhase] = useState<EnrichmentPhase>('idle');
  const [enrichResult, setEnrichResult] = useState<{
    leadId?: string;
    name?: string;
    score?: number;
    error?: string;
    outcome?: 'completed' | 'partial_success' | 'insufficient_data';
    signalGap?: EnrichmentStatusEvent['signalGap'];
  }>({});
  const [enrichTarget, setEnrichTarget] = useState<{ profileUrl: string; name: string } | null>(
    null,
  );
  const enrichJobIdRef = useRef<string | null>(null);
  const enrichInvalidatedRef = useRef(false);
  const triggerEnrichment = useTriggerWorkflow();

  type WebSearchPhase = 'idle' | 'searching' | 'results' | 'no_results';
  const [webSearchPhase, setWebSearchPhase] = useState<WebSearchPhase>('idle');
  const [webSearchResult, setWebSearchResult] = useState<WebSearchResultType | null>(null);
  const triggerWebSearch = useTriggerWebSearch();
  const confirmWebSearch = useConfirmWebSearch();
  const cancelWebSearch = useCancelWebSearch();
  const cachedResults = useWebSearchResults(
    id as string,
    currentOrganization?.id ?? '',
    webSearchPhase === 'idle' && !!id && !!currentOrganization?.id && !cancelWebSearch.isPending,
  );

  const [cancellingLogId, setCancellingLogId] = useState<string | null>(null);
  const [showCallMethod, setShowCallMethod] = useState(false);

  const orgId = currentOrganization?.id ?? '';
  const leadId = id as string;

  const { data: callStatus, refetch: refetchCallStatus } = useQuery({
    queryKey: ['auto-call-status', leadId, orgId],
    queryFn: () => voiceApi.getLeadCallStatus(orgId, leadId),
    enabled: !!orgId && !!leadId && !!lead?.phone,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.isPending) return 3000;
      return false;
    },
  });

  const handleOpenCallMethod = useCallback(() => {
    if (!lead?.phone) return;
    setShowCallMethod(true);
  }, [lead?.phone]);

  const handleCallHuman = useCallback(
    (phone: string) => {
      setShowCallMethod(false);
      const dialTarget = `tel:${phone.replace(/[^\d+]/g, '')}`;
      if (Platform.OS === 'web') {
        window.open(dialTarget, '_self');
      } else {
        Linking.openURL(dialTarget).catch(() => {
          showError(t('leads.detail.callAction.dialFailed'));
        });
      }
      try {
        trackEvent(mp, 'lead.call_human_initiated', { leadId });
      } catch (_) {
        /* analytics non-critical */
      }
    },
    [mp, leadId, showError, t],
  );

  const handleCallAgent = useCallback(() => {
    setShowCallMethod(false);
    try {
      trackEvent(mp, 'lead.call_agent_selected', { leadId });
    } catch (_) {
      /* analytics non-critical */
    }
    router.push(`/voice-agents/calls?triggerLeadId=${leadId}`);
  }, [mp, leadId, router]);

  const handleCancelCall = useCallback(
    async (logId: string) => {
      if (!orgId) return;
      setCancellingLogId(logId);
      try {
        await voiceApi.cancelAutoCall(logId, orgId);
        refetchCallStatus();
      } finally {
        setCancellingLogId(null);
      }
    },
    [orgId, refetchCallStatus],
  );

  const handleSseEvent = useCallback(
    (event: EnrichmentStatusEvent) => {
      if (enrichTarget && event.profileUrl && event.profileUrl !== enrichTarget.profileUrl) return;

      if (
        event.status === 'completed' ||
        event.status === 'partial_success' ||
        event.status === 'insufficient_data'
      ) {
        if (event.skipped) {
          setEnrichResult({ leadId: event.leadId });
          setEnrichPhase('exists');
        } else {
          setEnrichPhase('completed');
          setEnrichResult({
            leadId: event.leadId,
            name: event.name,
            score: event.score,
            outcome: event.status,
            signalGap: event.signalGap,
          });
          if (!enrichInvalidatedRef.current) {
            enrichInvalidatedRef.current = true;
            invalidateLeadDetail(id);
          }
        }
      } else if (event.status === 'failed' || event.status === 'auth_failed') {
        setEnrichPhase('failed');
        setEnrichResult({
          error: resolveEnrichmentFailureMessage(event, t),
        });
      } else {
        setEnrichPhase(event.status);
      }
    },
    [t, enrichTarget, queryClient, id],
  );

  const enrichOutcomeTone = (() => {
    if (enrichResult.outcome === 'partial_success') return colors.status.warning;
    if (enrichResult.outcome === 'insufficient_data') return colors.status.info;
    return colors.status.success;
  })();
  const enrichOutcomeTitleKey = (() => {
    if (enrichResult.outcome === 'partial_success') return 'workflow.progress.partialSuccess';
    if (enrichResult.outcome === 'insufficient_data') return 'workflow.progress.insufficientData';
    return 'leads.detail.enrichmentProgress.completed';
  })();

  const isEnrichmentActive =
    enrichPhase === 'submitted' ||
    enrichPhase === 'scraping' ||
    enrichPhase === 'scraping_comments' ||
    enrichPhase === 'analyzing' ||
    enrichPhase === 'persisting';

  useEffect(() => {
    if (!isEnrichmentActive || !currentOrganization?.id) return;

    const jobId = enrichJobIdRef.current;
    if (!jobId) return;

    const interval = setInterval(async () => {
      try {
        const { data } = await workflowsAPI.getJobStatus(jobId, currentOrganization.id);

        if (data.state === 'completed' && data.result) {
          setEnrichPhase('completed');
          setEnrichResult({
            leadId: data.result.leadId as string,
            name: data.result.name as string,
            score: data.result.score as number,
          });
          if (!enrichInvalidatedRef.current) {
            enrichInvalidatedRef.current = true;
            invalidateLeadDetail(id);
          }
        } else if (data.state === 'failed') {
          setEnrichPhase('failed');
          setEnrichResult({
            error: resolveEnrichmentFailureMessage({ status: 'failed', error: data.error }, t),
          });
        }
      } catch {
        // polling error, keep trying
      }
    }, 5_000);

    const timeout = setTimeout(() => {
      setEnrichPhase('failed');
      setEnrichResult({ error: 'Enrichment timed out' });
    }, 120_000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isEnrichmentActive, currentOrganization?.id, queryClient, id, t]);

  useEnrichmentStream({
    organizationId: currentOrganization?.id ?? '',
    enabled: isEnrichmentActive,
    onEvent: handleSseEvent,
    getAccessToken,
  });

  const handleWebSearchEvent = useCallback(
    (event: WebSearchStatusEvent) => {
      if (event.leadId && event.leadId !== id) return;

      if (event.status === 'awaiting_approval') {
        const result: WebSearchResultType = {
          leadId: event.leadId || id || '',
          contacts: (event.contacts as ExtractedContact[]) || [],
          totalLinksExplored: event.totalLinksExplored || 0,
          searchQuery: event.searchQuery || '',
        };
        setWebSearchResult(result);
        setWebSearchPhase('results');
      } else if (event.status === 'no_results') {
        setWebSearchResult({
          leadId: event.leadId || id || '',
          contacts: [],
          totalLinksExplored: event.totalLinksExplored || 0,
          searchQuery: event.searchQuery || '',
        });
        setWebSearchPhase('results');
        trackEvent(mp, 'web_search.no_results', {
          links_explored: event.totalLinksExplored,
        });
      } else if (event.status === 'completed') {
        setWebSearchPhase('idle');
        trackEvent(mp, 'web_search.completed', {
          contacts_found: event.contacts?.length,
          links_explored: event.totalLinksExplored,
        });
      } else if (event.status === 'failed') {
        setWebSearchPhase('idle');
        trackEvent(mp, 'web_search.failed', { error_message: event.error });
      } else if (event.status === 'cancelled') {
        setWebSearchPhase('idle');
      }
    },
    [id, mp],
  );

  const isWebSearchActive = webSearchPhase === 'searching';
  const [webSearchLastEvent, setWebSearchLastEvent] = useState<WebSearchStatusEvent | null>(null);

  const handleWebSearchStreamEvent = useCallback(
    (event: WebSearchStatusEvent) => {
      setWebSearchLastEvent(event);
      handleWebSearchEvent(event);
    },
    [handleWebSearchEvent],
  );

  useWebSearchStream({
    organizationId: currentOrganization?.id ?? '',
    enabled: isWebSearchActive,
    onEvent: handleWebSearchStreamEvent,
    getAccessToken,
  });

  useEffect(() => {
    if (webSearchPhase !== 'idle' || !cachedResults.data?.results) return;
    const cached = cachedResults.data.results;
    if (cached.contacts.length > 0) {
      setWebSearchResult(cached as unknown as WebSearchResultType);
      setWebSearchPhase('results');
    }
  }, [webSearchPhase, cachedResults.data]);

  const handleTriggerWebSearch = useCallback(async () => {
    if (!currentOrganization?.id || !id) return;
    if (!lead?.company) return;

    setWebSearchResult(null);
    setWebSearchPhase('searching');
    try {
      await triggerWebSearch.mutateAsync({
        organizationId: currentOrganization.id,
        leadId: id,
        existingEmail: lead?.email || undefined,
        existingPhone: lead?.phone || undefined,
      });
    } catch {
      setWebSearchPhase('idle');
    }
  }, [currentOrganization?.id, id, lead?.company, triggerWebSearch, lead?.email, lead?.phone]);

  const handleConfirmWebSearch = useCallback(
    async (contacts: ExtractedContact[], force?: boolean) => {
      if (!currentOrganization?.id || !id) return;
      await confirmWebSearch.mutateAsync({
        organizationId: currentOrganization.id,
        leadId: id,
        contacts: contacts.map((c) => ({
          type: c.type,
          value: c.value,
          sourceUrl: c.sourceUrl,
        })),
        ...(force && { force: true }),
      });
      setWebSearchPhase('idle');
      setWebSearchResult(null);
      queryClient.removeQueries({ queryKey: ['web-search-results', id] });
    },
    [currentOrganization?.id, id, confirmWebSearch, queryClient],
  );

  const handleCancelWebSearch = useCallback(async () => {
    if (!currentOrganization?.id || !id) return;
    await cancelWebSearch.mutateAsync({
      leadId: id,
      organizationId: currentOrganization.id,
    });
    setWebSearchPhase('idle');
  }, [currentOrganization?.id, id, cancelWebSearch]);

  const handleRefreshPosts = useCallback(
    async (target?: RefreshTarget) => {
      if (!id) return;
      setRefreshStatus(null);
      try {
        if (target?.type === 'contact' && target.sourceUrl) {
          await rescanContact.mutateAsync({ leadId: id, sourceUrl: target.sourceUrl });
        } else {
          const result = await refreshPosts.mutateAsync({ leadId: id });
          setRefreshStatus(result.status);
        }
      } catch {
        setRefreshStatus('error');
      }
    },
    [id, refreshPosts.mutateAsync, rescanContact.mutateAsync],
  );

  const handleEnrichCommenter = useCallback(
    async (profileUrl: string, name: string) => {
      if (!currentOrganization?.id) return;

      const safeProfileUrl = resolveCommenterProfileUrl(profileUrl);
      if (!safeProfileUrl) {
        setEnrichTarget(null);
        setEnrichResult({ error: t('leads.detail.enrichmentProgress.missingProfileId') });
        setEnrichPhase('failed');
        return;
      }

      setEnrichTarget({ profileUrl: safeProfileUrl, name });
      setEnrichPhase('checking');
      setEnrichResult({});
      enrichInvalidatedRef.current = false;

      try {
        const { data } = await workflowsAPI.findLeadBySourceUrl(
          currentOrganization.id,
          safeProfileUrl,
        );
        const action = resolveCommenterEnrichmentAction(data, safeProfileUrl);

        if (action.kind === 'navigate') {
          setEnrichResult({ leadId: action.leadId });
          setEnrichPhase('exists');
          return;
        }
        if (action.kind === 'blocked') {
          setEnrichPhase('exists');
          return;
        }

        setEnrichPhase('submitted');
        const response = await triggerEnrichment.mutateAsync({
          organizationId: currentOrganization.id,
          profileUrl: safeProfileUrl,
          socialMedia: action.platform,
          name,
        });
        enrichJobIdRef.current = response.jobId;
      } catch {
        setEnrichPhase('failed');
        setEnrichResult({ error: 'Failed to trigger enrichment' });
      }
    },
    [currentOrganization?.id, triggerEnrichment, t],
  );

  const handleRetryEnrichment = useCallback(() => {
    if (!enrichTarget) return;
    try {
      trackEvent(mp, 'lead.enrichment_retried', { leadId: id });
    } catch (_) {
      /* analytics non-critical */
    }
    handleEnrichCommenter(enrichTarget.profileUrl, enrichTarget.name);
  }, [enrichTarget, handleEnrichCommenter, mp, id]);

  const deepLinkHandledRef = useRef(false);

  useEffect(() => {
    if (deepLinkHandledRef.current) return;
    if (!currentOrganization?.id) return;

    const deepLink = resolveCommenterDeepLink({ source, publicId });
    if (deepLink.kind === 'none') return;

    deepLinkHandledRef.current = true;

    if (deepLink.kind === 'missing-id') {
      setEnrichTarget(null);
      setEnrichResult({ error: t('leads.detail.enrichmentProgress.missingProfileId') });
      setEnrichPhase('failed');
      return;
    }

    const commenterName = firstParam(commenterNameParam) ?? '';
    try {
      trackEvent(mp, 'lead.enrichment_deeplink_triggered', { leadId: id });
    } catch (_) {
      /* analytics non-critical */
    }
    handleEnrichCommenter(deepLink.profileUrl, commenterName);
  }, [
    currentOrganization?.id,
    source,
    publicId,
    commenterNameParam,
    t,
    handleEnrichCommenter,
    mp,
    id,
  ]);

  const handleMoveStage = useCallback(
    (stageId: string) => {
      if (!id) return;
      moveStage.mutate({ leadId: id, stageId });
      setShowStageModal(false);
    },
    [id, moveStage.mutate],
  );

  const handleAddNote = useCallback(
    async (content: string) => {
      if (!id) return;
      await addNote.mutateAsync({ leadId: id, content });
    },
    [id, addNote.mutateAsync],
  );

  const handleForceQualify = useCallback(() => {
    if (!id || !forceQualifyReason.trim()) return;
    const targetStageId = pendingMoveStageId;
    forceQualify.mutate(
      { leadId: id, reason: forceQualifyReason.trim() },
      {
        onSuccess: () => {
          setShowForceQualifyModal(false);
          setForceQualifyReason('');
          setPendingMoveStageId(null);
          if (targetStageId) {
            moveStage.mutate({ leadId: id, stageId: targetStageId });
          }
        },
        onError: () => {},
      },
    );
  }, [id, forceQualifyReason, forceQualify, pendingMoveStageId, moveStage.mutate]);

  const handleDelete = useCallback(async () => {
    const confirmed = await confirm({
      title: t('leads.detail.deleteTitle'),
      message: t('leads.detail.deleteConfirm'),
      confirmLabel: t('common.actions.delete'),
      variant: 'destructive',
    });
    if (!confirmed || !id) return;
    await deleteLead.mutateAsync(id);
    router.replace('/leads');
  }, [id, deleteLead.mutateAsync, router, t, confirm]);

  const handleTagsChange = useCallback(
    (nextTags: string[]) => {
      if (!id) return;
      updateLead.mutate({ id, payload: { tags: nextTags } });
    },
    [id, updateLead.mutate],
  );

  const tagSuggestions = useMemo(() => {
    const cached = queryClient.getQueriesData<{ leads?: Array<{ tags?: string[] }> }>({
      queryKey: ['leads'],
    });
    const all = new Set<string>();
    for (const [, value] of cached) {
      for (const cachedLead of value?.leads ?? []) {
        for (const tag of cachedLead.tags ?? []) {
          if (!tag.startsWith('seg:')) all.add(tag);
        }
      }
    }
    return [...all];
  }, [queryClient]);

  if (authLoading || isLoading) {
    return (
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          paddingHorizontal: tokens.spacing.lg,
          paddingTop: tokens.spacing.lg,
          paddingBottom: tokens.spacing['2xl'],
        }}
        showsVerticalScrollIndicator={false}
      >
        <LeadDetailSkeleton />
      </ScrollView>
    );
  }

  if (!isAuthenticated) return null;

  if (error || !lead) {
    return (
      <Box flex={1} alignItems="center" justifyContent="center">
        <Text variant="h6" color={colors.status.error.main}>
          {t('leads.detail.notFound')}
        </Text>
        <Button variant="ghost" size="sm" onPress={goBack} style={{ marginTop: tokens.spacing.md }}>
          {t('common.actions.goBack')}
        </Button>
      </Box>
    );
  }

  const memberships: Array<{
    organization?: { id: string };
    organizationId?: string;
    role: string;
  }> =
    ((backendUser as unknown as Record<string, unknown>)
      ?.organizationMembers as typeof memberships) ??
    ((backendUser as unknown as Record<string, unknown>)?.organizations as typeof memberships) ??
    [];
  const orgMembership = memberships.find(
    (o) => (o.organization?.id ?? o.organizationId) === currentOrganization?.id,
  );
  if (__DEV__ && memberships.length === 0 && backendUser) {
    console.warn('[ForceQualify] Could not resolve organization membership from backendUser');
  }

  const canForceQualify =
    ['owner', 'admin'].includes(orgMembership?.role ?? '') &&
    lead.qualificationStatus !== 'qualified';

  const isAdmin = ['owner', 'admin'].includes(orgMembership?.role ?? '');

  const qualificationBadge = (() => {
    const status = lead.qualificationStatus;
    if (!status) return null;
    const config: Record<string, { label: string; badgeColor: 'green' | 'red' | 'amber' }> = {
      qualified: {
        label: t('leads.detail.qualification.qualified'),
        badgeColor: 'green',
      },
      not_qualified_title: {
        label: t('leads.detail.qualification.notQualified'),
        badgeColor: 'red',
      },
      pending_validation: {
        label: t('leads.detail.qualification.pending'),
        badgeColor: 'amber',
      },
    };
    const c = config[status];
    if (!c) return null;
    const label = lead.normalizedJobTitle ? `${c.label} · ${lead.normalizedJobTitle}` : c.label;
    return (
      <Badge variant="subtle" color={c.badgeColor} size="sm">
        {label}
      </Badge>
    );
  })();

  // JSONB accepts any JSON value, so this can arrive as a string or as an
  // object whose keys are the indices of one — see asCustomFields.
  const customFields = asCustomFields(lead.customFields);
  const analysisResult = customFields.analysisResult as AnalysisResult | undefined;
  const companyLinkedinPosts = customFields.companyLinkedinPosts as LinkedInPostData[] | undefined;
  const instagramPosts = collectInstagramPosts(lead);
  const instagramReels = collectInstagramReels(lead);
  const instagramRecommendations = collectInstagramRecommendations(lead);
  const postsLastRefreshedAt = customFields.postsLastRefreshedAt as string | undefined;
  const scrapedCompanyProfile = customFields.companyProfile as CompanyProfileData | undefined;
  const companyAnalysis = customFields.companyAnalysis as CompanyAnalysisData | undefined;
  const companyProfile: CompanyProfileData | undefined =
    scrapedCompanyProfile ?? (lead.company ? { companyName: lead.company } : undefined);
  const { name: headerName, company: headerCompany } = getLeadHeaderIdentity(
    lead,
    t('common.fallbacks.unknownLead'),
  );
  const companyUrls = readCompanyUrls(customFields);
  // Only the networks the lead's own sourceUrl does not already cover, so the
  // existing SOURCE URL row is never duplicated.
  const leadUrlPlatform = getLeadProfileUrls({ sourceUrl: lead.sourceUrl }).at(0)?.platform;
  const otherProfileUrls = getLeadProfileUrls(lead).filter(
    ({ platform }) => platform !== leadUrlPlatform,
  );
  // Enrichment for a network the lead's own fields do not represent, read off the
  // contact row it was written to.
  const contactEnrichments = collectContactEnrichments(lead);
  const isInstagram = lead.sourceUrl?.includes('instagram.com') ?? false;
  const isBrand = isBrandLead(lead);
  const brandCategory = getBrandCategory(lead);
  const brandBio = getBrandBio(lead);
  const brandBadge = isBrand ? (
    <Badge
      testID="lead_detail_brand_badge"
      variant="subtle"
      color="purple"
      size="sm"
      icon={<Building2 size={12} color={colors.accent.purple[300]} strokeWidth={2} />}
      accessibilityLabel={
        brandCategory
          ? `${t('leads.detail.brand.badge')}: ${brandCategory}`
          : t('leads.detail.brand.badge')
      }
    >
      {brandCategory
        ? `${t('leads.detail.brand.badge')} · ${brandCategory}`
        : t('leads.detail.brand.badge')}
    </Badge>
  ) : null;

  const contactsWithPosts = (lead.contacts ?? [])
    .map((c) => {
      const cf = asCustomFields(c.customFields);
      const posts = cf.linkedinPosts as LinkedInPostData[] | undefined;
      return { name: c.name, sourceUrl: c.sourceUrl, isPrimary: c.isPrimary, posts: posts ?? [] };
    })
    .filter((c) => c.posts.length > 0);

  // Not gated on the lead's own platform. A lead enriched on Instagram first keeps
  // an Instagram sourceUrl, and gating on it hid LinkedIn posts that were sitting
  // on a contact row. The Instagram card next to this one has always rendered
  // whenever it had content; this one now does the same.
  const hasCarouselPosts =
    (companyLinkedinPosts && companyLinkedinPosts.length > 0) || contactsWithPosts.length > 0;

  const hasEnrichmentData = ENRICHMENT_FIELD_KEYS.some(
    (key) => customFields[key] != null && customFields[key] !== '',
  );
  const enrichmentData = hasEnrichmentData
    ? (Object.fromEntries(
        ENRICHMENT_FIELD_KEYS.filter((k) => customFields[k] != null).map((k) => [
          k,
          customFields[k],
        ]),
      ) as EnrichmentData)
    : undefined;

  const canRefreshPosts = !!lead.sourceUrl;

  const AGENT_CUSTOM_FIELD_KEYS = [
    'qualificationScore',
    'qualificationLevel',
    'qualificationReasoning',
    'qualificationActions',
    'qualifiedAt',
    'sentiment',
    'enrichment',
    'followUpPlan',
    'followUpUrgency',
    'nextFollowUpDate',
    'prospectIntake',
    'firstContact',
    'deepQualification',
    'proposalPrep',
    'wonOnboarding',
    'lostAnalysis',
    'enrichmentStatus',
  ];

  const hiddenCustomFieldKeys = new Set([
    'analysisResult',
    'productInterest',
    'suggestedProductInterest',
    'productInterestId',
    'productInterestLinkKey',
    'suggestedProductId',
    'suggestedProductLinkKey',
    'suggestedProductConfidence',
    'linkedinPosts',
    'companyLinkedinPosts',
    'instagramPosts',
    'instagramReels',
    'recommendations',
    'instagramProfile',
    'postsLastRefreshedAt',
    'contactName',
    'companyProfile',
    'companyAnalysis',
    'webSearchEnrichment',
    ...ENRICHMENT_FIELD_KEYS,
    ...AGENT_CUSTOM_FIELD_KEYS,
  ]);
  const otherCustomFields = Object.entries(customFields).filter(
    ([key]) => !hiddenCustomFieldKeys.has(key),
  );
  const stageControl = (
    <Button
      testID="lead_detail_stage_button"
      variant="outline"
      size="sm"
      pill
      onPress={() => setShowStageModal(true)}
      style={isMobile ? { paddingHorizontal: tokens.spacing.md } : undefined}
    >
      <HStack spacing="xs" align="center">
        <Box width={8} height={8} borderRadius={4} backgroundColor={lead.currentStage.color} />
        <Text
          variant="caption"
          color={colors.text.primary}
          numberOfLines={1}
          style={[{ fontWeight: '500' }, isMobile && { maxWidth: 96 }]}
        >
          {lead.currentStage.name}
        </Text>
      </HStack>
    </Button>
  );

  const headerActionButtons = (
    <>
      <IconButton
        testID="lead_detail_edit_button"
        icon={<Edit3 size={16} color={colors.text.secondary} strokeWidth={2} />}
        variant="outline"
        size="md"
        onPress={() => router.push(`/lead/edit/${id}`)}
        accessibilityLabel="Edit lead"
        tooltip="Edit"
      />
      {lead.phone && (
        <IconButton
          icon={<PhoneCall size={16} color={colors.primary[400]} strokeWidth={2} />}
          variant="outline"
          size="md"
          onPress={handleOpenCallMethod}
          accessibilityLabel={t('leads.detail.callAction.label')}
          tooltip={t('leads.detail.callAction.tooltip')}
        />
      )}
      {lead.email && (
        <IconButton
          icon={<Mail size={16} color={colors.primary[400]} strokeWidth={2} />}
          variant="outline"
          size="md"
          onPress={() => {
            if (Platform.OS === 'web') {
              window.open(`mailto:${lead.email}`, '_self');
            }
          }}
          accessibilityLabel="Email lead"
          tooltip="Email"
        />
      )}
      {canForceQualify && (
        <IconButton
          testID="lead_detail_force_qualify_button"
          icon={<ShieldCheck size={16} color={colors.status.success.main} strokeWidth={2} />}
          variant="outline"
          size="md"
          onPress={() => setShowForceQualifyModal(true)}
          accessibilityLabel={t('leads.detail.forceQualify.button')}
          tooltip={t('leads.detail.forceQualify.button')}
        />
      )}
      <IconButton
        testID="lead_detail_delete_button"
        icon={<Trash2 size={16} color={colors.status.error.main} strokeWidth={2} />}
        variant="ghost"
        size="md"
        onPress={handleDelete}
        accessibilityLabel="Delete lead"
        tooltip="Delete"
      />
    </>
  );

  return (
    <>
      <ScrollView
        testID="lead_detail_screen"
        style={{ flex: 1 }}
        // Match the dashboard tab's content inset (lg horizontal + top). Bottom
        // stays '4xl' to clear the mobile tab bar — '2xl' (32) was too tight,
        // the last card sat flush against the tab bar.
        contentContainerStyle={{
          paddingHorizontal: tokens.spacing.lg,
          paddingTop: tokens.spacing.lg,
          paddingBottom: tokens.spacing['4xl'],
        }}
        showsVerticalScrollIndicator={false}
      >
        <VStack spacing="xl">
          {/* Header — one card, so the lead identity reads the same way a
              LeadCard does in Sales Pipeline and the Leads list. */}
          <SectionCard>
            {isMobile ? (
              <VStack spacing="md">
                <HStack spacing="md" align="center">
                  <IconButton
                    icon={<ArrowLeft size={18} color={colors.text.secondary} strokeWidth={2} />}
                    variant="subtle"
                    size="md"
                    onPress={goBack}
                    accessibilityLabel="Go back"
                    tooltip="Go back"
                  />

                  <Avatar size="xl" name={headerName} />

                  <VStack spacing="xxs" style={{ flex: 1, minWidth: 0 }}>
                    <Text
                      testID="lead_detail_name_text"
                      variant="h5"
                      color={colors.text.primary}
                      numberOfLines={1}
                      style={{ fontWeight: '700' }}
                    >
                      {headerName}
                    </Text>
                    {!isBrand && headerCompany && (
                      <Text variant="bodySmall" color={colors.text.secondary} numberOfLines={1}>
                        {headerCompany}
                      </Text>
                    )}
                    {isBrand ? brandBadge : qualificationBadge}
                  </VStack>
                </HStack>

                <HStack spacing="sm" align="center" wrap style={{ width: '100%' }}>
                  {stageControl}
                  {headerActionButtons}
                </HStack>
              </VStack>
            ) : (
              <HStack spacing="md" align="center">
                <IconButton
                  icon={<ArrowLeft size={18} color={colors.text.secondary} strokeWidth={2} />}
                  variant="subtle"
                  size="md"
                  onPress={goBack}
                  accessibilityLabel="Go back"
                  tooltip="Go back"
                />

                <Avatar size="xl" name={headerName} />

                <VStack spacing="xxs" style={{ flex: 1, minWidth: 0 }}>
                  <Text
                    testID="lead_detail_name_text"
                    variant="h4"
                    color={colors.text.primary}
                    numberOfLines={1}
                    style={{ fontWeight: '700' }}
                  >
                    {headerName}
                  </Text>
                  {!isBrand && headerCompany && (
                    <Text variant="bodySmall" color={colors.text.secondary} numberOfLines={1}>
                      {headerCompany}
                    </Text>
                  )}
                  {isBrand ? brandBadge : qualificationBadge}
                </VStack>

                <HStack spacing="xs" align="center">
                  {stageControl}
                  {headerActionButtons}
                </HStack>
              </HStack>
            )}
          </SectionCard>

          {/* Stage Progress */}
          {lead.funnelTemplate?.stages && (
            <SectionCard>
              <StageProgress
                stages={lead.funnelTemplate.stages}
                currentStageId={lead.currentStageId}
                onStagePress={(stageId) => handleMoveStage(stageId)}
              />
            </SectionCard>
          )}

          {/* Call Status Banner */}
          {callStatus && (
            <CallStatusBanner
              status={callStatus}
              onRetry={handleOpenCallMethod}
              onCancel={handleCancelCall}
              isCancelling={cancellingLogId === callStatus.id}
            />
          )}

          {/* Main Content */}
          <Stack direction={isDesktop ? 'horizontal' : 'vertical'} spacing="xl">
            {/* Left Column - Info */}
            {/* flex:1 only applies in the horizontal (desktop) layout, where it
                splits row WIDTH between the two columns. In the vertical
                (mobile) layout the Stack is a flex column, so flex:1 would
                instead split HEIGHT 50/50 — forcing each column to half the
                container height and overflowing the taller column's content
                over the other. Content-sized height is correct when stacked. */}
            <VStack
              testID="lead_detail_left_column"
              spacing="xl"
              style={isDesktop ? { flex: 1 } : undefined}
            >
              {/* Contact Info */}
              <SectionCard title={t('leads.detail.contactInfo')}>
                {/* lg, not xl. Ten label/value pairs at 24px apart is what made
                    this column twice the height of the content in it. */}
                <VStack spacing="lg">
                  <InfoField
                    icon={<Building2 size={16} color={colors.text.tertiary} strokeWidth={1.5} />}
                    label={t('leads.detail.company')}
                    value={lead.company}
                  />
                  <InfoField
                    testID="lead_detail_job_title"
                    icon={<Briefcase size={16} color={colors.text.tertiary} strokeWidth={1.5} />}
                    label={t('leads.detail.jobTitle')}
                    value={lead.jobTitle}
                  />
                  <InfoField
                    icon={<Mail size={16} color={colors.text.tertiary} strokeWidth={1.5} />}
                    label={t('leads.detail.email')}
                    value={lead.email || '\u2014'}
                  />
                  {lead.phone && (
                    <InfoField
                      icon={<Phone size={16} color={colors.text.tertiary} strokeWidth={1.5} />}
                      label={t('leads.detail.phone')}
                      value={lead.phone}
                    />
                  )}
                  {lead.sourceUrl && (
                    <InfoField
                      icon={
                        <ExternalLink size={16} color={colors.text.tertiary} strokeWidth={1.5} />
                      }
                      label={t('leads.detail.sourceUrl')}
                      value={lead.sourceUrl}
                    />
                  )}
                  {/*
                    A lead enriched on a second network keeps its original
                    sourceUrl — the new profile lands on its own contact row — so
                    without these rows the second URL was stored and never shown.
                  */}
                  {otherProfileUrls.map(({ platform, url }) => (
                    <InfoField
                      key={platform}
                      testID={`lead_detail_profile_url_${platform}`}
                      icon={<PlatformIcon platform={platform} />}
                      label={t('leads.detail.otherProfileUrl', {
                        platform: t(`common.sources.${platform}`),
                      })}
                      value={url}
                    />
                  ))}
                  <InfoField
                    testID="lead_detail_company_website"
                    icon={<Globe size={16} color={colors.text.tertiary} strokeWidth={1.5} />}
                    label={t('leads.detail.companyWebsiteUrl')}
                    value={companyUrls.websiteUrl}
                  />
                  <InfoField
                    testID="lead_detail_company_linkedin"
                    icon={<LinkedinIcon size={16} color={colors.text.tertiary} />}
                    label={t('leads.detail.companyLinkedinUrl')}
                    value={companyUrls.linkedinUrl}
                  />

                  {lead.company && (
                    <WebSearchButton
                      onPress={handleTriggerWebSearch}
                      loading={triggerWebSearch.isPending || webSearchPhase === 'searching'}
                      disabled={webSearchPhase === 'searching'}
                    />
                  )}

                  <HStack spacing="md" align="center">
                    <Globe size={16} color={colors.text.tertiary} strokeWidth={1.5} />
                    <VStack spacing="xxs" style={{ flex: 1 }}>
                      <Text variant="label" uppercase color={colors.text.tertiary}>
                        {t('leads.detail.source')}
                      </Text>
                      <LeadSourceBadges lead={lead} showLabel />
                    </VStack>
                  </HStack>

                  <ProductInterestField
                    value={customFields.productInterest as string | undefined}
                    valueReference={lead.productReferences?.interest}
                    suggestion={customFields.suggestedProductInterest as string | undefined}
                    suggestionReference={lead.productReferences?.suggestion}
                    suggestionConfidence={
                      customFields.suggestedProductConfidence === 'low' ? 'low' : 'high'
                    }
                    onSave={(next, productId) => {
                      updateLead.mutate({
                        id: lead.id,
                        payload: {
                          customFields: productId
                            ? { productInterest: next, productInterestId: productId }
                            : { productInterest: next },
                        },
                      });
                    }}
                    isSaving={updateLead.isPending}
                  />

                  <ReviewFlagBanner
                    requiresReview={lead.requiresReview === true}
                    reason={lead.requiresReviewReason}
                    flaggedAt={lead.requiresReviewAt}
                  />

                  <ConsentSection leadId={lead.id} canManage={isAdmin} />

                  <MarketingConsentSection
                    leadId={lead.id}
                    marketingOptIn={lead.marketingOptIn}
                    canManage={isAdmin}
                  />

                  {!isBrand && (
                    <HStack
                      testID="lead_detail_assignee_row"
                      spacing="md"
                      align="center"
                      style={isAdmin ? { zIndex: 20, position: 'relative' } : undefined}
                    >
                      <User size={16} color={colors.text.tertiary} strokeWidth={1.5} />
                      <VStack spacing="xxs" style={{ flex: 1 }}>
                        <Text variant="label" uppercase color={colors.text.tertiary}>
                          {t('leads.detail.assignedTo')}
                        </Text>
                        {isAdmin ? (
                          <AssigneePicker
                            organizationId={lead.organizationId}
                            value={lead.assignedTo ?? null}
                            onChange={(userId) => {
                              updateLead.mutate({
                                id: lead.id,
                                payload: { assignedTo: userId },
                              });
                            }}
                          />
                        ) : (
                          <Text
                            testID="lead_detail_assignee_name"
                            variant="body"
                            color={colors.text.primary}
                          >
                            {lead.assignedUser?.fullName ?? t('leads.assigneePicker.unassigned')}
                          </Text>
                        )}
                      </VStack>
                    </HStack>
                  )}

                  <LeadTagsEditor
                    tags={lead.tags}
                    onChange={handleTagsChange}
                    suggestions={tagSuggestions}
                    isSaving={updateLead.isPending}
                  />

                  {lead.lastContactedAt && (
                    <HStack spacing="md" align="center">
                      <Clock size={16} color={colors.text.tertiary} strokeWidth={1.5} />
                      <VStack spacing="xxs">
                        <Text variant="label" uppercase color={colors.text.tertiary}>
                          {t('leads.detail.lastContacted')}
                        </Text>
                        <Text variant="body" color={colors.text.primary}>
                          {new Date(lead.lastContactedAt).toLocaleDateString()}
                        </Text>
                      </VStack>
                    </HStack>
                  )}
                </VStack>
              </SectionCard>

              {/* Contact Carousel (contacts + enrichment per contact) */}
              {lead.contacts && lead.contacts.length > 0 ? (
                <ContactCarouselCard leadId={lead.id} contacts={lead.contacts} />
              ) : isEnrichmentProcessing && !enrichmentData ? (
                <SectionCard>
                  <HStack spacing="sm" align="center" justify="center">
                    <ActivityIndicator size="small" color={colors.primary[500]} />
                    <Text
                      variant="bodySmall"
                      color={colors.text.secondary}
                      style={{ fontWeight: '500' }}
                    >
                      {t('leads.detail.enrichmentProcessing')}
                    </Text>
                  </HStack>
                </SectionCard>
              ) : (
                enrichmentData && (
                  <EnrichmentProfileCard data={enrichmentData} isBrand={isBrand} bio={brandBio} />
                )
              )}

              {/*
                The lead carries one flat set of enrichment fields, written only
                when the scraped profile is the primary contact. A second network
                enriched afterwards lands on its own contact row, so without this
                its profile data stayed invisible. Scoring is deliberately left
                out — see collectContactEnrichments.
              */}
              {contactEnrichments.map(({ platform, data }) => (
                <SectionCard
                  key={platform}
                  testID={`lead_detail_contact_enrichment_${platform}`}
                  title={t('leads.detail.otherNetworkEnrichment', {
                    platform: t(`common.sources.${platform}`),
                  })}
                >
                  {/* isBrand drives the brand variant of the gauge. Without it a
                      business account's brandScore renders under the individual
                      buyer-potential label, which is the wrong question. */}
                  <EnrichmentProfileCard data={data} isBrand={data.profileKind === 'brand'} />
                </SectionCard>
              ))}

              {/* Custom Fields */}
              {otherCustomFields.length > 0 && (
                <SectionCard>
                  <CustomFieldsCard fields={otherCustomFields} />
                </SectionCard>
              )}

              {/* Company Profile */}
              {!isEnrichmentProcessing && companyProfile && (
                <SectionCard>
                  <CompanyProfileCard data={companyProfile} analysis={companyAnalysis} />
                </SectionCard>
              )}

              {/* Lead Analysis — individual prospect qualification. Brand leads
                  use the brand analysis in EnrichmentProfileCard instead. */}
              {!isBrand && !isEnrichmentProcessing && !pipelineRunning && analysisResult && (
                <AnalysisCard analysis={analysisResult} />
              )}
            </VStack>

            {/* Right Column - Activity, Posts & Notes */}
            <VStack
              testID="lead_detail_right_column"
              spacing="xl"
              style={isDesktop ? { flex: 1 } : undefined}
            >
              {/* AI Agent Insights */}
              {/* The one glowing element on this screen. `accent` is the
                  design system's selected treatment — teal border plus halo —
                  so the halo no longer has to be hand-rolled here. */}
              <SectionCard variant="accent">
                <AgentInsightsCard leadId={id} organizationId={lead.organizationId} />
              </SectionCard>

              {/* LinkedIn Activity Carousel (company + contacts posts) */}
              {hasCarouselPosts && (
                <LinkedInActivityCarouselCard
                  companyName={lead.company || lead.fullName}
                  companyPosts={companyLinkedinPosts}
                  contacts={contactsWithPosts}
                  onRefresh={canRefreshPosts ? handleRefreshPosts : undefined}
                  isRefreshing={refreshPosts.isPending || rescanContact.isPending}
                  onEnrichCommenter={enrichPhase === 'idle' ? handleEnrichCommenter : undefined}
                />
              )}

              {/* Instagram content — paginated by piece, with posts, reels and the
                  recommendations split across tabs so the section stays short
                  (FLOSEAI-625). Sources are lead-level or an enriched contact's. */}
              <InstagramContentCarouselCard
                posts={instagramPosts}
                reels={instagramReels}
                recommendations={instagramRecommendations}
                onRefresh={isInstagram && canRefreshPosts ? handleRefreshPosts : undefined}
                isRefreshing={refreshPosts.isPending}
                refreshStatus={refreshStatus}
                postsLastRefreshedAt={postsLastRefreshedAt}
                onEnrichCommenter={enrichPhase === 'idle' ? handleEnrichCommenter : undefined}
              />

              {/* Notes */}
              <SectionCard title={t('leads.detail.notes')}>
                <NotesList
                  notes={lead.notes}
                  onAddNote={handleAddNote}
                  loading={addNote.isPending}
                />
              </SectionCard>

              {/* Call Results */}
              <SectionCard title={t('leads.callResults.sectionTitle')}>
                <CallResultsSection organizationId={lead.organizationId} leadId={lead.id} />
              </SectionCard>

              {/* Activity Timeline */}
              <SectionCard title={t('leads.detail.activity')}>
                <LeadActivitySection leadId={lead.id} />
              </SectionCard>
            </VStack>
          </Stack>
        </VStack>
      </ScrollView>

      {showStageModal && lead.funnelTemplate?.stages && (
        <StageMoveModal
          visible={showStageModal}
          onClose={() => setShowStageModal(false)}
          stages={lead.funnelTemplate.stages}
          currentStageId={lead.currentStageId}
          onSelectStage={handleMoveStage}
          loading={moveStage.isPending}
        />
      )}

      {enrichPhase !== 'idle' && (
        <Sheet
          visible
          onClose={() => {
            if (
              enrichPhase === 'completed' ||
              enrichPhase === 'failed' ||
              enrichPhase === 'exists'
            ) {
              setEnrichPhase('idle');
              setEnrichTarget(null);
              enrichJobIdRef.current = null;
            }
          }}
          position="center"
          size="sm"
          hideClose
        >
          <View>
            {(enrichPhase === 'completed' ||
              enrichPhase === 'failed' ||
              enrichPhase === 'exists') && (
              <Pressable
                onPress={() => {
                  setEnrichPhase('idle');
                  setEnrichTarget(null);
                  enrichJobIdRef.current = null;
                }}
                accessibilityLabel={t('common.actions.close')}
                style={{ position: 'absolute', top: 0, right: 0, zIndex: 1, padding: 4 }}
              >
                <X size={18} color={colors.text.tertiary} strokeWidth={2} />
              </Pressable>
            )}
            <VStack spacing="lg" style={{ alignItems: 'center' }}>
              {enrichPhase === 'checking' && (
                <>
                  <ActivityIndicator size="large" color={colors.primary[500]} />
                  <Text variant="h6" color={colors.text.primary} style={{ fontWeight: '600' }}>
                    {t('leads.detail.enrichmentProgress.checking')}
                  </Text>
                  <Text variant="body" color={colors.text.secondary}>
                    {enrichTarget?.name}
                  </Text>
                </>
              )}

              {enrichPhase === 'exists' && (
                <>
                  <AlertCircle size={40} color={colors.status.warning.main} strokeWidth={1.5} />
                  <Box
                    backgroundColor={colors.status.warning.bg}
                    borderRadius="md"
                    padding="lg"
                    borderWidth={1}
                    borderColor={colors.status.warning.main}
                    style={{ width: '100%', alignItems: 'center' }}
                  >
                    <Text
                      variant="h6"
                      color={colors.status.warning.dark}
                      style={{ fontWeight: '600' }}
                    >
                      {t('workflow.leadExists.title')}
                    </Text>
                    <Text
                      variant="bodySmall"
                      color={colors.text.secondary}
                      style={{ marginTop: 8, textAlign: 'center' }}
                    >
                      {t('workflow.leadExists.description')}
                    </Text>
                  </Box>
                  {enrichResult.leadId && (
                    <Pressable
                      onPress={() => {
                        const leadId = enrichResult.leadId;
                        setEnrichPhase('idle');
                        setEnrichTarget(null);
                        enrichJobIdRef.current = null;
                        router.push(`/lead/${leadId}`);
                      }}
                      style={{
                        backgroundColor: colors.primary[500],
                        borderRadius: tokens.radius.md,
                        paddingHorizontal: tokens.spacing.xl,
                        paddingVertical: tokens.spacing.md,
                      }}
                      accessibilityLabel={t('workflow.leadExists.viewLead')}
                      accessibilityRole="button"
                    >
                      <Text
                        variant="body"
                        color={colors.primary[900]}
                        style={{ fontWeight: '600' }}
                      >
                        {t('workflow.leadExists.viewLead')}
                      </Text>
                    </Pressable>
                  )}
                  <Pressable
                    onPress={() => {
                      setEnrichPhase('idle');
                      setEnrichTarget(null);
                      enrichJobIdRef.current = null;
                    }}
                    accessibilityLabel={t('common.actions.close')}
                  >
                    <Text variant="bodySmall" color={colors.text.tertiary}>
                      {t('common.actions.close')}
                    </Text>
                  </Pressable>
                </>
              )}

              {isEnrichmentActive && (
                <>
                  <Text variant="h6" color={colors.text.primary} style={{ fontWeight: '600' }}>
                    {t('leads.detail.enrichmentProgress.title')}
                  </Text>
                  <Text variant="bodySmall" color={colors.text.secondary}>
                    {enrichTarget?.name}
                  </Text>
                  <VStack spacing="sm" style={{ width: '100%' }}>
                    {(enrichTarget?.profileUrl?.includes('instagram.com')
                      ? (['scraping', 'scraping_comments', 'analyzing', 'persisting'] as const)
                      : (['scraping', 'analyzing', 'persisting'] as const)
                    ).map((step, index) => {
                      const steps = enrichTarget?.profileUrl?.includes('instagram.com')
                        ? ['scraping', 'scraping_comments', 'analyzing', 'persisting']
                        : ['scraping', 'analyzing', 'persisting'];
                      const stepIndex = steps.indexOf(enrichPhase as string);
                      const isActive =
                        enrichPhase === step || (enrichPhase === 'submitted' && index === 0);
                      const isDone = stepIndex > index;
                      return (
                        <HStack
                          key={step}
                          spacing="md"
                          align="center"
                          style={{
                            padding: tokens.spacing.sm,
                            borderRadius: tokens.radius.md,
                            backgroundColor: isActive ? colors.primary.muted : 'transparent',
                          }}
                        >
                          {isActive && (
                            <ActivityIndicator size="small" color={colors.primary[500]} />
                          )}
                          {!isActive && isDone && (
                            <CheckCircle
                              size={16}
                              color={colors.status.success.main}
                              strokeWidth={2}
                            />
                          )}
                          {!isActive && !isDone && (
                            <Text variant="body" color={colors.text.tertiary}>
                              {index + 1}
                            </Text>
                          )}
                          <Text
                            variant="body"
                            color={isActive || isDone ? colors.text.primary : colors.text.tertiary}
                          >
                            {t(`workflow.progress.${step}`)}
                          </Text>
                        </HStack>
                      );
                    })}
                  </VStack>
                </>
              )}

              {enrichPhase === 'completed' && (
                <>
                  <CheckCircle size={40} color={enrichOutcomeTone.main} strokeWidth={1.5} />
                  <Box
                    backgroundColor={enrichOutcomeTone.bg}
                    borderRadius="md"
                    padding="lg"
                    borderWidth={1}
                    borderColor={enrichOutcomeTone.main}
                    style={{ width: '100%', alignItems: 'center' }}
                  >
                    <Text variant="h6" color={enrichOutcomeTone.main} style={{ fontWeight: '600' }}>
                      {t(enrichOutcomeTitleKey)}
                    </Text>
                    {enrichResult.name && (
                      <Text variant="body" color={enrichOutcomeTone.dark} style={{ marginTop: 8 }}>
                        {enrichResult.name}
                      </Text>
                    )}
                    {enrichResult.signalGap && (
                      <Text
                        variant="bodySmall"
                        color={colors.text.secondary}
                        style={{ marginTop: 4 }}
                      >
                        {t(`leads.enrichment.signalGap.${enrichResult.signalGap}`)}
                      </Text>
                    )}
                    {enrichResult.outcome !== 'insufficient_data' &&
                      enrichResult.score !== undefined && (
                        <Text
                          variant="bodySmall"
                          color={colors.text.secondary}
                          style={{ marginTop: 4 }}
                        >
                          {t('workflow.progress.score')}: {enrichResult.score}
                        </Text>
                      )}
                  </Box>
                  {enrichResult.leadId && (
                    <Pressable
                      onPress={() => {
                        setEnrichPhase('idle');
                        setEnrichTarget(null);
                        enrichJobIdRef.current = null;
                        router.push(`/lead/${enrichResult.leadId}`);
                      }}
                      style={{
                        backgroundColor: colors.primary[500],
                        borderRadius: tokens.radius.md,
                        paddingHorizontal: tokens.spacing.xl,
                        paddingVertical: tokens.spacing.md,
                      }}
                      accessibilityLabel={t('leads.detail.enrichmentProgress.viewLead')}
                      accessibilityRole="button"
                    >
                      <Text
                        variant="body"
                        color={colors.primary[900]}
                        style={{ fontWeight: '600' }}
                      >
                        {t('leads.detail.enrichmentProgress.viewLead')}
                      </Text>
                    </Pressable>
                  )}
                  <Pressable
                    onPress={() => {
                      setEnrichPhase('idle');
                      setEnrichTarget(null);
                      enrichJobIdRef.current = null;
                    }}
                    accessibilityLabel={t('common.actions.close')}
                  >
                    <Text variant="bodySmall" color={colors.text.tertiary}>
                      {t('common.actions.close')}
                    </Text>
                  </Pressable>
                </>
              )}

              {enrichPhase === 'failed' && (
                <EnrichmentFailedActions
                  errorMessage={enrichResult.error}
                  onRetry={enrichTarget ? handleRetryEnrichment : undefined}
                  onClose={() => {
                    setEnrichPhase('idle');
                    setEnrichTarget(null);
                    enrichJobIdRef.current = null;
                  }}
                />
              )}
            </VStack>
          </View>
        </Sheet>
      )}

      {webSearchPhase === 'searching' && (
        <WebSearchProgressSheet
          visible
          onClose={() => {
            setWebSearchPhase('idle');
            queryClient.removeQueries({ queryKey: ['web-search-results', id] });
            if (currentOrganization?.id && id) {
              cancelWebSearch.mutate({ leadId: id, organizationId: currentOrganization.id });
            }
          }}
          onCancel={handleCancelWebSearch}
          lastEvent={webSearchLastEvent}
          leadName={lead?.fullName || undefined}
        />
      )}

      {webSearchPhase === 'results' && (
        <WebSearchResultsSheet
          visible
          onClose={() => {
            setWebSearchPhase('idle');
            setWebSearchResult(null);
            queryClient.removeQueries({ queryKey: ['web-search-results', id] });
            if (currentOrganization?.id && id) {
              cancelWebSearch.mutate({ leadId: id, organizationId: currentOrganization.id });
            }
          }}
          onConfirm={handleConfirmWebSearch}
          onRetry={handleTriggerWebSearch}
          result={webSearchResult}
          isConfirming={confirmWebSearch.isPending}
          existingEmail={lead?.email || undefined}
          existingPhone={lead?.phone || undefined}
        />
      )}
      <CallMethodSheet
        visible={showCallMethod}
        onClose={() => setShowCallMethod(false)}
        lead={lead}
        onCallHuman={handleCallHuman}
        onCallAgent={handleCallAgent}
      />

      <Sheet
        visible={showForceQualifyModal}
        onClose={() => {
          setShowForceQualifyModal(false);
          setForceQualifyReason('');
          setPendingMoveStageId(null);
        }}
        title={t('leads.detail.forceQualify.title')}
        size="sm"
        position="center"
      >
        <VStack testID="force-qualify-modal" spacing="md">
          <Text variant="bodySmall" color={colors.text.secondary}>
            {t('leads.detail.forceQualify.description')}
          </Text>
          <FormInput
            style={forceQualifyStyles.reasonInput}
            placeholder={t('leads.detail.forceQualify.reasonPlaceholder')}
            value={forceQualifyReason}
            onChangeText={setForceQualifyReason}
            multiline
            numberOfLines={3}
          />
          {forceQualify.isError && (
            <Text variant="caption" color={colors.status.error.main}>
              {(forceQualify.error as { response?: { data?: { message?: string } } })?.response
                ?.data?.message || t('leads.detail.forceQualify.error')}
            </Text>
          )}
          <HStack spacing="sm" justify="end">
            <Button
              variant="ghost"
              size="sm"
              onPress={() => {
                setShowForceQualifyModal(false);
                setForceQualifyReason('');
                setPendingMoveStageId(null);
              }}
            >
              {t('common.actions.cancel')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onPress={handleForceQualify}
              loading={forceQualify.isPending}
              disabled={!forceQualifyReason.trim()}
            >
              {t('leads.detail.forceQualify.confirm')}
            </Button>
          </HStack>
        </VStack>
      </Sheet>
    </>
  );
}

const forceQualifyStyles = StyleSheet.create({
  reasonInput: {
    backgroundColor: colors.background.input,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    padding: tokens.spacing.md,
    color: colors.text.primary,
    fontSize: tokens.fontSize.base,
    minHeight: 80,
    textAlignVertical: 'top',
  },
});
